'use client';

import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AddIcon, RemoveIcon } from '@/shared/ui/display/EmojiIcon';
import type {
  AttrDef,
  AttrDefType,
  AttributeClause,
  ConditionRule,
  RuleType,
} from '../model/moving-item.types';
import {
  SPEC_LEVEL_ATTR_INDEX,
  SPEC_LEVEL_ATTR_LABEL,
  TYPE_DISPLAY,
} from '../model/moving-item.types';
import { normalizeRuleForType } from '../model/sanitizeConditionRule';
import { isRangeAnd } from '../lib/conditionToText';

const RULE_TYPE_LABELS: Record<RuleType, string> = {
  ALWAYS_MATCH: '항상',
  NEVER_MATCH: '절대 안 함',
  NUMBER_GTE: '이상(≥)',
  NUMBER_GT: '초과(>)',
  NUMBER_LTE: '이하(≤)',
  NUMBER_LT: '미만(<)',
  BOOLEAN_EQ: '참/거짓',
  ENUM_IN: '다음 중 하나',
  AND: 'AND',
  CROSS_AND: 'CROSS_AND',
};

const RULE_TYPES_BY_ATTR: Record<AttrDefType, RuleType[]> = {
  STRING: ['ALWAYS_MATCH', 'NEVER_MATCH'],
  NUMBER: ['ALWAYS_MATCH', 'NEVER_MATCH', 'NUMBER_GTE', 'NUMBER_GT', 'NUMBER_LTE', 'NUMBER_LT'],
  BOOLEAN: ['ALWAYS_MATCH', 'NEVER_MATCH', 'BOOLEAN_EQ'],
  ENUM: ['ALWAYS_MATCH', 'NEVER_MATCH', 'ENUM_IN'],
};

const NUMBER_COMPARE_TYPES: RuleType[] = ['NUMBER_GTE', 'NUMBER_GT', 'NUMBER_LTE', 'NUMBER_LT'];

function EvalAttrSelectInline({
  allAttrDefs,
  ownerAttrIndex,
  onMoveEvalAttr,
}: {
  allAttrDefs: AttrDef[];
  ownerAttrIndex: number;
  onMoveEvalAttr?: (toAttrIndex: number) => void;
}) {
  if (!onMoveEvalAttr) return null;
  return (
    <FormControl variant="filled" size="small" sx={{ minWidth: 160, maxWidth: 260, flexShrink: 0 }}>
      <InputLabel id="moving-item-eval-attr-label">평가 속성</InputLabel>
      <Select
        labelId="moving-item-eval-attr-label"
        label="평가 속성"
        value={ownerAttrIndex}
        onChange={(e) => onMoveEvalAttr(Number(e.target.value))}
      >
        <MenuItem value={SPEC_LEVEL_ATTR_INDEX}>{SPEC_LEVEL_ATTR_LABEL} (속성 무관)</MenuItem>
        {allAttrDefs.map((a, i) => (
          <MenuItem key={i} value={i} disabled={!a.key.trim()}>
            {a.name?.trim() || a.key || '(키 없음)'} ({TYPE_DISPLAY[a.type]})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export type RuleMode = 'simple' | 'range' | 'andList' | 'compound';

export function deriveRuleMode(rule: ConditionRule, attrType: AttrDefType): RuleMode {
  if (rule.type === 'CROSS_AND') return 'compound';
  if (rule.type === 'AND') {
    if (attrType === 'NUMBER' && isRangeAnd(rule)) return 'range';
    return 'andList';
  }
  return 'simple';
}

function parseRange(rule: ConditionRule): { min: number | ''; max: number | '' } {
  const gte = rule.rules?.find((r) => r.type === 'NUMBER_GTE');
  const lt = rule.rules?.find((r) => r.type === 'NUMBER_LT');
  return {
    min: gte?.threshold ?? '',
    max: lt?.threshold ?? '',
  };
}

function serializeRange(min: number | '', max: number | ''): ConditionRule {
  return {
    type: 'AND',
    rules: [
      { type: 'NUMBER_GTE', threshold: min === '' ? 0 : min },
      { type: 'NUMBER_LT', threshold: max === '' ? 0 : max },
    ],
  };
}

function LeafRuleEditor({
  rule,
  attrType,
  attrValues = [],
  sentenceSubject,
  onChange,
}: {
  rule: ConditionRule;
  attrType: AttrDefType;
  attrValues?: string[];
  /** 문장 앞에 붙는 속성 라벨 (CROSS_AND 절에서는 생략 가능) */
  sentenceSubject?: string;
  onChange: (next: ConditionRule) => void;
}) {
  const ruleOptions = RULE_TYPES_BY_ATTR[attrType];

  const handleEnumToggle = (val: string) => {
    const values = rule.values ?? [];
    const next = values.includes(val) ? values.filter((v) => v !== val) : [...values, val];
    onChange(normalizeRuleForType({ type: 'ENUM_IN', values: next.length ? next : [] }));
  };

  const numberCompareValue = NUMBER_COMPARE_TYPES.includes(rule.type as RuleType)
    ? (rule.type as (typeof NUMBER_COMPARE_TYPES)[number])
    : 'NUMBER_GTE';

  const alwaysNever = rule.type === 'ALWAYS_MATCH' || rule.type === 'NEVER_MATCH';

  if (alwaysNever) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
        <Typography variant="body2" color="text.primary">
          {sentenceSubject ? (
            <>
              <strong>{sentenceSubject}</strong>
              {rule.type === 'ALWAYS_MATCH'
                ? ' — 이 조건은 항상 참입니다.'
                : ' — 이 조건은 절대 참이 아닙니다.'}
            </>
          ) : rule.type === 'ALWAYS_MATCH' ? (
            '이 조건은 항상 참입니다.'
          ) : (
            '이 조건은 절대 참이 아닙니다.'
          )}
        </Typography>
        <FormControl variant="filled" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>규칙</InputLabel>
          <Select
            label="규칙"
            value={rule.type}
            onChange={(e) => {
              const t = e.target.value as RuleType;
              onChange(
                normalizeRuleForType({
                  type: t,
                  threshold: undefined,
                  value: undefined,
                  values: undefined,
                } as ConditionRule)
              );
            }}
          >
            {ruleOptions.map((rt) => (
              <MenuItem key={rt} value={rt}>
                {RULE_TYPE_LABELS[rt]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {sentenceSubject && (
        <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
          {sentenceSubject}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" component="span">
        가
      </Typography>

      {attrType === 'NUMBER' && (
        <>
          <TextField
            variant="filled"
            size="small"
            type="number"
            label="값"
            value={rule.type.startsWith('NUMBER_') ? (rule.threshold ?? '') : ''}
            onChange={(e) => {
              const t = e.target.value ? Number(e.target.value) : undefined;
              const rt = rule.type.startsWith('NUMBER_') ? rule.type : 'NUMBER_GTE';
              onChange(normalizeRuleForType({ type: rt, threshold: t } as ConditionRule));
            }}
            sx={{ width: 96 }}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={rule.type.startsWith('NUMBER_') ? numberCompareValue : 'NUMBER_GTE'}
            onChange={(_, v) => {
              if (!v) return;
              onChange(
                normalizeRuleForType({
                  type: v as RuleType,
                  threshold: rule.threshold,
                } as ConditionRule)
              );
            }}
          >
            {NUMBER_COMPARE_TYPES.map((t) => (
              <ToggleButton key={t} value={t} sx={{ px: 1, minWidth: 40 }}>
                {t === 'NUMBER_GTE' && '≥'}
                {t === 'NUMBER_GT' && '>'}
                {t === 'NUMBER_LTE' && '≤'}
                {t === 'NUMBER_LT' && '<'}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            또는
          </Typography>
          <Button size="small" variant="text" onClick={() => onChange({ type: 'ALWAYS_MATCH' })}>
            항상
          </Button>
          <Button size="small" variant="text" onClick={() => onChange({ type: 'NEVER_MATCH' })}>
            절대 안 함
          </Button>
        </>
      )}

      {attrType !== 'NUMBER' && (
        <FormControl variant="filled" size="small" sx={{ minWidth: 120 }}>
          <InputLabel>비교</InputLabel>
          <Select
            label="비교"
            value={rule.type}
            onChange={(e) => {
              const t = e.target.value as RuleType;
              onChange(
                normalizeRuleForType({
                  type: t,
                  threshold: undefined,
                  value: undefined,
                  values: undefined,
                } as ConditionRule)
              );
            }}
          >
            {ruleOptions.map((rt) => (
              <MenuItem key={rt} value={rt}>
                {RULE_TYPE_LABELS[rt]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {attrType === 'BOOLEAN' && rule.type === 'BOOLEAN_EQ' && (
        <FormControl variant="filled" size="small" sx={{ minWidth: 100 }}>
          <InputLabel>값</InputLabel>
          <Select
            label="값"
            value={rule.value === true ? 'true' : 'false'}
            onChange={(e) =>
              onChange(
                normalizeRuleForType({ type: 'BOOLEAN_EQ', value: e.target.value === 'true' })
              )
            }
          >
            <MenuItem value="true">참</MenuItem>
            <MenuItem value="false">거짓</MenuItem>
          </Select>
        </FormControl>
      )}

      {attrType === 'ENUM' && rule.type === 'ENUM_IN' && attrValues.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            중 하나:
          </Typography>
          {attrValues.filter(Boolean).map((v) => (
            <FormControlLabel
              key={v}
              control={
                <Checkbox
                  size="small"
                  checked={(rule.values ?? []).includes(v)}
                  onChange={() => handleEnumToggle(v)}
                />
              }
              label={v}
            />
          ))}
        </Box>
      )}

      {attrType === 'ENUM' && rule.type === 'ENUM_IN' && attrValues.length === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="span"
          sx={{ maxWidth: 320, lineHeight: 1.4 }}
        >
          선택할 ENUM 값이 없습니다. 속성 정의에서 해당 속성의 옵션 목록을 먼저 추가하세요.
        </Typography>
      )}

      <Typography variant="body2" color="text.secondary" component="span">
        이면
      </Typography>
    </Box>
  );
}

export interface SentenceRuleEditorProps {
  rule: ConditionRule;
  attrType: AttrDefType;
  attrValues?: string[];
  allAttrDefs: AttrDef[];
  /** 스펙 attributes 배열에서의 인덱스 */
  ownerAttrIndex: number;
  /** 이 조건이 매달린 속성의 표시 라벨 */
  ownerAttrLabel: string;
  onChange: (rule: ConditionRule) => void;
  /** 다른 속성으로 조건 저장 위치 변경 */
  onMoveEvalAttr?: (toAttrIndex: number) => void;
}

export function SentenceRuleEditor({
  rule,
  attrType,
  attrValues = [],
  allAttrDefs,
  ownerAttrIndex,
  ownerAttrLabel,
  onChange,
  onMoveEvalAttr,
}: SentenceRuleEditorProps) {
  const mode = useMemo(() => deriveRuleMode(rule, attrType), [rule, attrType]);
  const range = useMemo(() => parseRange(rule), [rule]);

  const unsupportedAnd =
    rule.type === 'AND' &&
    attrType === 'NUMBER' &&
    !isRangeAnd(rule) &&
    (rule.rules?.length ?? 0) > 0;

  const handleModeChange = (next: RuleMode) => {
    if (next === 'simple') {
      onChange({ type: 'ALWAYS_MATCH' });
      return;
    }
    if (next === 'range' && attrType === 'NUMBER') {
      onChange(serializeRange('', ''));
      return;
    }
    if (next === 'andList') {
      onChange({
        type: 'AND',
        rules: [{ type: 'ALWAYS_MATCH' }, { type: 'ALWAYS_MATCH' }],
      });
      return;
    }
    if (next === 'compound') {
      const firstKey = allAttrDefs.find((a) => a.key.trim())?.key ?? '';
      onChange({
        type: 'CROSS_AND',
        clauses: [{ key: firstKey, rule: { type: 'ALWAYS_MATCH' } }],
      });
    }
  };

  const updateAndRuleAt = (index: number, sub: ConditionRule) => {
    if (rule.type !== 'AND' || !rule.rules) return;
    const rules = [...rule.rules];
    rules[index] = normalizeRuleForType(sub);
    onChange({ type: 'AND', rules });
  };

  const addAndRule = () => {
    if (rule.type !== 'AND' || !rule.rules) return;
    onChange({ type: 'AND', rules: [...rule.rules, { type: 'ALWAYS_MATCH' }] });
  };

  const removeAndRule = (index: number) => {
    if (rule.type !== 'AND' || !rule.rules || rule.rules.length <= 1) return;
    onChange({ type: 'AND', rules: rule.rules.filter((_, i) => i !== index) });
  };

  const updateClause = (index: number, patch: Partial<AttributeClause>) => {
    if (rule.type !== 'CROSS_AND' || !rule.clauses) return;
    const clauses = [...rule.clauses];
    clauses[index] = { ...clauses[index], ...patch };
    if (patch.rule) {
      clauses[index].rule = normalizeRuleForType(patch.rule);
    }
    onChange({ type: 'CROSS_AND', clauses });
  };

  const addClause = () => {
    if (rule.type !== 'CROSS_AND' || !rule.clauses) return;
    onChange({
      type: 'CROSS_AND',
      clauses: [...rule.clauses, { key: '', rule: { type: 'ALWAYS_MATCH' } }],
    });
  };

  const removeClause = (index: number) => {
    if (rule.type !== 'CROSS_AND' || !rule.clauses || rule.clauses.length <= 1) return;
    onChange({ type: 'CROSS_AND', clauses: rule.clauses.filter((_, i) => i !== index) });
  };

  const hasKeyedAttr = allAttrDefs.some((a) => a.key.trim());

  const modeOptions: { value: RuleMode; label: string }[] = [
    { value: 'simple', label: '단순 조건' },
    ...(attrType === 'NUMBER'
      ? ([
          { value: 'range' as const, label: '범위 조건 (min~max)' },
          { value: 'andList' as const, label: '동일 속성 AND (복수 규칙)' },
        ] satisfies { value: RuleMode; label: string }[])
      : []),
    // 참조할 속성이 없으면 CROSS_AND는 의미가 없다
    ...(hasKeyedAttr
      ? ([{ value: 'compound' as const, label: '복합 조건 (여러 속성, CROSS_AND)' }] satisfies {
          value: RuleMode;
          label: string;
        }[])
      : []),
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="subtitle2" color="primary">
        IF (조건)
      </Typography>

      <FormControl variant="filled" size="small" sx={{ minWidth: 220 }}>
        <InputLabel>조건 유형</InputLabel>
        <Select
          label="조건 유형"
          value={mode}
          onChange={(e) => handleModeChange(e.target.value as RuleMode)}
        >
          {modeOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {unsupportedAnd && (
        <Alert severity="warning">
          범위(이상+미만) 형식이 아닌 AND 규칙입니다. 조건 유형을 다시 선택하거나 하위 규칙을
          편집하세요.
        </Alert>
      )}

      {mode === 'simple' && (
        <Box
          sx={{
            pl: 0.5,
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            minHeight: 56,
          }}
        >
          <EvalAttrSelectInline
            allAttrDefs={allAttrDefs}
            ownerAttrIndex={ownerAttrIndex}
            onMoveEvalAttr={onMoveEvalAttr}
          />
          <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
            <LeafRuleEditor
              rule={rule}
              attrType={attrType}
              attrValues={attrValues}
              sentenceSubject={ownerAttrLabel}
              onChange={onChange}
            />
          </Box>
        </Box>
      )}

      {mode === 'range' && attrType === 'NUMBER' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            pl: 0.5,
            minHeight: 56,
            rowGap: 1,
          }}
        >
          <EvalAttrSelectInline
            allAttrDefs={allAttrDefs}
            ownerAttrIndex={ownerAttrIndex}
            onMoveEvalAttr={onMoveEvalAttr}
          />
          <Typography variant="body2" fontWeight={500}>
            {ownerAttrLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            가
          </Typography>
          <TextField
            variant="filled"
            size="small"
            type="number"
            label="이상 (≥)"
            value={range.min === '' ? '' : range.min}
            onChange={(e) => {
              const min = e.target.value === '' ? '' : Number(e.target.value);
              const max = range.max === '' ? '' : range.max;
              onChange(serializeRange(min, max));
            }}
            sx={{ width: 110 }}
          />
          <Typography variant="body2" color="text.secondary">
            이상
          </Typography>
          <TextField
            variant="filled"
            size="small"
            type="number"
            label="미만 (&lt;)"
            value={range.max === '' ? '' : range.max}
            onChange={(e) => {
              const max = e.target.value === '' ? '' : Number(e.target.value);
              const min = range.min === '' ? '' : range.min;
              onChange(serializeRange(min, max));
            }}
            sx={{ width: 110 }}
          />
          <Typography variant="body2" color="text.secondary">
            미만이면
          </Typography>
        </Box>
      )}

      {mode === 'andList' && rule.type === 'AND' && (!rule.rules || rule.rules.length === 0) && (
        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, minHeight: 48 }}
        >
          <EvalAttrSelectInline
            allAttrDefs={allAttrDefs}
            ownerAttrIndex={ownerAttrIndex}
            onMoveEvalAttr={onMoveEvalAttr}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              onChange({ type: 'AND', rules: [{ type: 'ALWAYS_MATCH' }, { type: 'ALWAYS_MATCH' }] })
            }
          >
            하위 규칙 초기화 (2개)
          </Button>
        </Box>
      )}

      {mode === 'andList' && rule.type === 'AND' && rule.rules && rule.rules.length > 0 && (
        <Box
          sx={{
            pl: 1,
            py: 1,
            borderLeft: 2,
            borderColor: 'divider',
            borderStyle: 'dashed',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, minHeight: 48 }}
          >
            <EvalAttrSelectInline
              allAttrDefs={allAttrDefs}
              ownerAttrIndex={ownerAttrIndex}
              onMoveEvalAttr={onMoveEvalAttr}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {ownerAttrLabel} — 동일 속성에 대해 모두 만족(AND)
          </Typography>
          {rule.rules.map((sub, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <LeafRuleEditor
                rule={sub}
                attrType={attrType}
                attrValues={attrValues}
                sentenceSubject={`${ownerAttrLabel} (조건 ${i + 1})`}
                onChange={(next) => updateAndRuleAt(i, next)}
              />
              <IconButton size="small" onClick={() => removeAndRule(i)} aria-label="하위 규칙 삭제">
                <RemoveIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addAndRule}
            sx={{ alignSelf: 'flex-start' }}
          >
            하위 규칙 추가
          </Button>
        </Box>
      )}

      {mode === 'compound' && rule.type === 'CROSS_AND' && rule.clauses && (
        <Box
          sx={{
            pl: 1,
            py: 1,
            borderLeft: 2,
            borderColor: 'primary.light',
            borderStyle: 'dashed',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, minHeight: 48 }}
          >
            <EvalAttrSelectInline
              allAttrDefs={allAttrDefs}
              ownerAttrIndex={ownerAttrIndex}
              onMoveEvalAttr={onMoveEvalAttr}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            다음 속성 조건을 모두 만족할 때 (CROSS_AND)
          </Typography>
          {rule.clauses.map((clause, i) => {
            const def = allAttrDefs.find((a) => a.key === clause.key);
            const enumVals = def?.type === 'ENUM' ? def.values?.filter(Boolean) : undefined;
            const subj = def ? def.name?.trim() || def.key : clause.key;

            return (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  p: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                  <FormControl variant="filled" size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>속성</InputLabel>
                    <Select
                      label="속성"
                      value={clause.key}
                      onChange={(e) => {
                        const key = e.target.value;
                        updateClause(i, {
                          key,
                          rule: { type: 'ALWAYS_MATCH' },
                        });
                      }}
                    >
                      {allAttrDefs.map((a, idx) => (
                        <MenuItem
                          key={a.key || `idx-${idx}`}
                          value={a.key}
                          disabled={!a.key.trim()}
                        >
                          {a.key || '(키 없음)'} {a.name ? `— ${a.name}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {def && (
                    <LeafRuleEditor
                      rule={clause.rule}
                      attrType={def.type}
                      attrValues={enumVals}
                      sentenceSubject={subj}
                      onChange={(next) => updateClause(i, { rule: next })}
                    />
                  )}
                  <IconButton size="small" onClick={() => removeClause(i)} aria-label="절 삭제">
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addClause}
            sx={{ alignSelf: 'flex-start' }}
          >
            속성 조건 추가
          </Button>
        </Box>
      )}
    </Box>
  );
}
